import { onAuthStateChanged } from 'firebase/auth';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { getValueFromStorage, setValueToStorage } from 'hooks/useLocalStorage';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { getClients } from 'services/ClientService';
import { jwtTokenSelector, loggedUserSelector } from 'store/user/selectors';
import { setClientId, setJwtToken, setUser, setUserRole } from 'store/user/userSlice';

import { auth } from 'configuration/firebase';
import { LocalStorageKeys } from 'constants/enums';
import { GENERAL_TOAST_OPTIONS } from 'constants/general';
import { handleApiErrors } from 'utils/errorUtils';
import { formatFbUser } from 'utils/formatters';

const AppProviders = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const loggedUser = useAppSelector(loggedUserSelector);
  const jwtToken = useAppSelector(jwtTokenSelector);
  const storageUser = getValueFromStorage(LocalStorageKeys.user, null) || {};
  const storageJwtToken = getValueFromStorage(LocalStorageKeys.jwtToken, null) || {};

  useEffect(() => {
    if (!loggedUser && !isEmpty(storageUser) && !jwtToken && !isEmpty(storageJwtToken)) {
      dispatch(setUser(storageUser));
      dispatch(setJwtToken(storageJwtToken));
    }
  }, [loggedUser, storageUser, storageUser, storageJwtToken]);

  useEffect(() => {
    if (loggedUser?.uid) {
      fetchClient(loggedUser?.uid);
    }
  }, [loggedUser?.uid]);

  const handlePermissionDenied = () => {
    auth.signOut();
    dispatch(setUser(null));
    dispatch(setJwtToken(null));
    setValueToStorage(LocalStorageKeys.jwtToken, null);
    setValueToStorage(LocalStorageKeys.user, null);
    toast.error(intl.formatMessage({ id: 'error.permissionDenied' }), GENERAL_TOAST_OPTIONS);
  };

  const fetchClient = async (loggedUserId: string) => {
    try {
      const clients = await getClients();

      const foundClient = clients.find(({ userId }) => userId === loggedUserId);

      if (!foundClient) {
        handlePermissionDenied();
      } else {
        dispatch(setUserRole(foundClient.role));
        dispatch(setClientId(foundClient.id));
      }
    } catch (e) {
      handleApiErrors(e);
      handlePermissionDenied();
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const formattedUser = formatFbUser(user);
          dispatch(setUser(formattedUser));

          const idToken = await user.getIdToken();

          if (idToken) {
            dispatch(setJwtToken({ idToken }));
          }
        } else {
          dispatch(setUser(null));
          dispatch(setJwtToken(null));
        }
      } catch (e) {
        handleApiErrors(e);
      }
    });

    return () => unsubscribe();
  }, []);

  return null;
};

export default AppProviders;
